"use client";

import { useState, type JSX } from 'react';
import { ContentBlock, add_blocks_metadata } from '@/features/editor/utils/content-blocks';
import Image from 'next/image';
import { Link } from "@/i18n/navigation";
import { Check } from "lucide-react";
import { VideoEmbed } from './VideoEmbed';
import { PodcastEmbed } from './PodcastEmbed';
import { QuoteCarousel } from "@/components/shared/quote_carousel";
import { GradientCircle } from "@/components/shared/gradient_circle";
import { BulletPointList } from "@/components/shared/bullet_point";
import { BlogBlock } from "@/components/shared/blog_block";
import { OutcomeMetricCards } from "@/features/content/components/OutcomeMetricCards";
import { CustomButton } from '@/components/ui/custom-button';
import { Banner } from "@/components/shared/banner";
import ShareAndAuthorInfo from '@/features/marketing/components/blog/ShareAndAuthorInfo';
import { EABCaseStudyFormModal, PerspectivePieceFormModal } from '@/components/modals/FormModal';
import { Sources } from '@/components/ui/sources';
import { CaseStudyReadCard } from './CaseStudyReadCard';
import { FAQ } from '@/components/ui/faq';

interface ContentRendererProps {
  blocks: ContentBlock[];
}

export function ContentRenderer({ blocks }: ContentRendererProps) {
  // Add metadata (id and order) to blocks
  const blocksWithMetadata = add_blocks_metadata(blocks);

  return (
    <div className="space-y-6 md:space-y-7">
      {blocksWithMetadata.map((block) => (
        <ContentBlockRenderer key={block.id} block={block} allBlocks={blocksWithMetadata} />
      ))}
    </div>
  );
}

function ContentBlockRenderer({ block, allBlocks }: { block: ContentBlock & { id: string; order: number }; allBlocks: (ContentBlock & { id: string; order: number })[] }) {
  switch (block.type) {
    case 'heading':
      const HeadingTag = `h${block.level}` as keyof JSX.IntrinsicElements;
      const headingClassNameByLevel = {
        1: 'mb-5! text-3xl! sm:text-4xl! lg:text-5xl! leading-tight',
        2: 'mt-10! mb-5! md:mt-12! md:mb-6! text-2xl! sm:text-3xl! lg:text-[2.15rem]! leading-tight',
        3: 'mt-8! mb-4! text-xl! sm:text-2xl! lg:text-[1.75rem]! leading-snug',
        4: 'mt-6! mb-3! text-lg! sm:text-xl! leading-snug',
        5: 'mt-6! mb-3! text-base! sm:text-lg! leading-snug',
        6: 'mt-5! mb-3! text-sm! sm:text-base! leading-snug',
      }[block.level];

      return (
        <HeadingTag 
          id={block.id}
          className={`${headingClassNameByLevel} ${block.className || ''}`}
        >
          {block.text}
        </HeadingTag>
      );

    case 'paragraph':
      return (
        <p 
          className={`text-base leading-7 text-gray-700 md:text-[1.05rem] ${block.className || ''}`}
          dangerouslySetInnerHTML={{ __html: block.text }}
        />
      );

    case 'image':
      return (
        <div className="my-6 md:my-8">
          <Image
            src={block.src}
            alt={block.alt}
            width={block.width || 800}
            height={block.height || 450}
            className={`rounded-lg ${block.className || ''}`}
          />
          {block.caption && (
            <p className="text-sm text-gray-500 mt-2 text-center italic">
              {block.caption}
            </p>
          )}
        </div>
      );

    case 'video':
      return (
        <div className="my-6 md:my-8 flex-col items-start flex">
          <VideoEmbed
            url={block.url}
            title={block.title}
            platform={block.platform}
          />
        </div>
      );

    case 'podcast':
      return (
        <div className="my-6 md:my-8">
          <PodcastEmbed
            url={block.embedUrl}
            title={block.title}
            platform={block.platform}
          />
          {block.description && (
            <p className="text-gray-500 mt-4">{block.description}</p>
          )}
        </div>
      );

    case 'quote':
      return (
        <div className="my-10">
          <QuoteCarousel
            quotes={[{
              text: block.text,
              author: block.author,
              title: block.title || '',
              image: block.image || '',
              logo: block.logo || ''
            }]}
            variant="blog-quote"
            className="mx-auto"
          />
        </div>
      );

    case 'statistics':
      if (block.layout === 'outcome-cards') {
        return (
          <div className="my-10! md:my-12!">
            <OutcomeMetricCards
              subtitle={block.stats[0]?.title}
              items={block.stats.map((stat) => ({
                value: stat.value,
                description: stat.description || "",
              }))}
            />
          </div>
        );
      }

      return (
        <div className={`my-10! grid items-stretch gap-6 xl:gap-8 ${block.layout === 'grid' ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3' : ''}`}>
          {block.stats.map((stat, index) => (
            <GradientCircle
              key={index}
              value={stat.value}
              title={stat.title}
              description={stat.description}
              color={stat.color}
              hasGradient={stat.hasGradient}
              gradientFrom={stat.gradientFrom}
              gradientTo={stat.gradientTo}
              className={
                block.layout === 'grid' && block.stats.length === 3 && index === 2
                  ? 'sm:col-span-2 md:col-span-1'
                  : ''
              }
            />
          ))}
        </div>
      );

    case 'comparison-columns':
      return (
        <div className="my-8 grid gap-6 md:grid-cols-2">
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm md:p-6">
            <h3 className="mb-3 text-xl font-semibold text-gray-900">{block.leftTitle}</h3>
            <p className="text-base leading-7 text-gray-700 md:text-[1.05rem]">{block.leftText}</p>
          </section>
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm md:p-6">
            <h3 className="mb-3 text-xl font-semibold text-gray-900">{block.rightTitle}</h3>
            <p className="text-base leading-7 text-gray-700 md:text-[1.05rem]">{block.rightText}</p>
          </section>
        </div>
      );

    case 'case-study-read-card':
      return (
        <CaseStudyReadCard
          title={block.title}
          href={block.href}
          imageSrc={block.imageSrc}
          imageAlt={block.imageAlt}
          ctaText={block.ctaText}
          className="my-8 lg:max-w-none!"
        />
      );

    case 'bullet-list':
      if (block.style === 'subpoints') {
        return (
          <div className="mt-1 mb-2 ml-8 space-y-2">
            {block.items.map((item, index) => (
              <div key={index} className="flex items-start gap-2">
                <span className="mt-1 flex size-4 shrink-0 items-center justify-center rounded-full bg-ss-light-green">
                  <Check className="size-2.5 stroke-[3] text-white" />
                </span>
                <span className="text-[15px] leading-6 text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        );
      }

      return (
        <div className="my-4">
          <BulletPointList items={block.items} />
        </div>
      );

    case 'faq':
      return (
        <div className="my-10 md:my-12">
          <FAQ
            title={block.title || 'Frequently asked questions'}
            items={block.items}
          />
        </div>
      );

    case 'blog-block':
      return (
        <div className="my-6 md:my-8">
          <BlogBlock
            title={block.title}
            subtitle={block.subtitle}
            bodyText={block.bodyText}
            value={block.value}
            description={block.description}
            color={block.color}
            rightImage={block.rightImage}
            hideCircle={block.hideCircle}
            bgColor={block.bgColor}
            layout={block.layout || "two-col"}
            hasGradient={block.hasGradient}
            gradientFrom={block.gradientFrom}
            gradientTo={block.gradientTo}
            hasGrainyEffect={block.hasGrainyEffect}
            showArrow={block.showArrow}
          />
        </div>
      );

    case 'cta-button':
      const [isButtonModalOpen, setIsButtonModalOpen] = useState(false);
      const href = block.href || "";
      const isExternalHref =
        href.startsWith("http://") ||
        href.startsWith("https://") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:");

      return (
        <div className="my-5">
          {block.showModal ? (
            <>
              <CustomButton
                variant={block.variant || "primary"}
                size={block.size || "lg"}
                onClick={() => setIsButtonModalOpen(true)}
              >
                {block.text}
              </CustomButton>
              {block.showModal === 'eab-case-study' && (
                <EABCaseStudyFormModal
                  open={isButtonModalOpen}
                  onOpenChange={setIsButtonModalOpen}
                />
              )}
              {block.showModal === 'perspective-piece' && (
                <PerspectivePieceFormModal
                  open={isButtonModalOpen}
                  onOpenChange={setIsButtonModalOpen}
                />
              )}
            </>
          ) : (
            <CustomButton
              asChild
              variant={block.variant || "primary"}
              size={block.size || "lg"}
            >
              {isExternalHref ? (
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {block.text}
                </a>
              ) : (
                <Link href={href}>
                  {block.text}
                </Link>
              )}
            </CustomButton>
          )}
        </div>
      );

    case 'spacer':
      const spacerHeight = {
        sm: 'h-4',
        md: 'h-8', 
        lg: 'h-16',
        xl: 'h-24'
      }[block.height];
      return <div className={spacerHeight} />;

    case 'banner':
      return (
        <div className="my-6 md:my-8">
          <Banner
            variant={block.variant || 'article'}
            title={block.title}
            description={block.description}
            authorName={block.authorName}
            authorTitle={block.authorTitle}
            authorImage={block.authorImage}
            publishDate={block.publishDate}
            readingTime={block.readingTime}
            articleImage={block.articleImage}
            articleImageAlt={block.articleImageAlt}
          />
        </div>
      );

    case 'share-and-author-info':
      return (
        <div className="my-6 md:my-8">
          <ShareAndAuthorInfo
            authorImageUrl={block.authorImageUrl}
            editorialInfo={block.editorialInfo}
          />
        </div>
      );

    case 'button-row':
      return (
        <div className="my-5 flex flex-wrap space-x-6">
          {block.buttons.map((button, index) => (
            <ContentBlockRenderer
              key={`${block.id}-button-${index}`}
              block={{ ...button, id: `${block.id}-button-${index}`, order: index }}
              allBlocks={allBlocks}
            />
          ))}
        </div>
      );

    case 'sources':
      return (
        <div className="my-10">
          <Sources
            items={block.items}
            title={block.title}
            showViewAll={block.showViewAll}
          />
        </div>
      );

    default:
      return null;
  }
}
